import React, { useState, useMemo, useEffect } from "react";
import Autosuggest from "react-autosuggest";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import TextField from "@material-ui/core/TextField";
import Paper from "@material-ui/core/Paper";
import MenuItem from "@material-ui/core/MenuItem";
import Popper from "@material-ui/core/Popper";
import { Field, change } from "redux-form";
import { makeStyles } from "@material-ui/core/styles";
import { Avatar, Chip } from "@material-ui/core";
import { Lock, LockOpen } from "@material-ui/icons";
import { fetchSponsorsAutocomplete } from "../redux/actions";
import { sponsorsAutocompleteSelector } from "../redux/selectors";
import { useConstant } from "../utilities/useConstant";
import { debounceMilliseconds } from "../utilities/constants";
var debounce = require("lodash/debounce");

const useASStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  container: {
    position: "relative",
  },
  suggestionsContainerOpen: {
    position: "absolute",
    zIndex: 1,
    marginTop: theme.spacing(1),
    left: 0,
    right: 0,
  },
  suggestion: {
    display: "block",
  },
  suggestionsList: {
    margin: 0,
    padding: 0,
    listStyleType: "none",
  },
  optionStyle: {
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    "& ul li .MuiMenuItem-root": {
      whiteSpace: "normal",
    },
  },
  divider: {
    height: theme.spacing(2),
  },
  chip: {
    color: "#000",
    backgroundColor: "#8CCFFF",
    fontSize: "13px",
    height: "auto",
    minHeight: "32px",
    "& .MuiChip-label": {
      whiteSpace: "normal",
    },
  },
  paper: {
    backgroundColor: "rgb(36, 48, 65)",
    marginTop: "15px",
    padding: "20px",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    maxHeight: "100px",
  },
}));

const styles = {
  input: {
    color: "#fff",
    fontSize: "1rem",
    backgroundColor: "rgb(36, 48, 65)",
  },

  root: {
    color: "#fff",
  },
  focused: {
    color: "#fff",
    backgroundColor: "rgb(36, 48, 65)",
    "&$focused": {
      color: "#fff",
      backgroundColor: "rgb(36, 48, 65)",
    },
  },
};

const useLabelStyles = makeStyles({
  root: {
    color: "#fff",
    marginLeft: "-2px",
  },
});

const useInputStyles = makeStyles({
  root: {
    color: "#fff",
    backgroundColor: "rgb(36, 48, 65)",
  },
  focused: {
    color: "#fff",
    backgroundColor: "rgb(36, 48, 65)",
    "&$focused": {
      color: "#fff",
      backgroundColor: "rgb(36, 48, 65)",
    },
  },
});

export const SponsorsMultiSelectComponent = ({
  label,
  meta: { initial, form, touched, invalid, error },
  input,
  noMatchText,
  allowArchived,
  defaultLabel,
}) => {
  const suggestions = useSelector(sponsorsAutocompleteSelector, shallowEqual);

  const dispatch = useDispatch();
  var labelStyles = useLabelStyles();
  var inputStyles = useInputStyles();
  const [noMatch, setNoMatch] = useState(false);
  const [matchSelected, setMatchSelected] = useState(false);
  const [stateSuggestions, setSuggestions] = useState(suggestions);
  const classes = useASStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [state, setState] = useState({
    label: "",
  });

  const handleSuggestionsFetchRequested = ({ value }) => {
    dispatch(fetchSponsorsAutocomplete(value, allowArchived));
  };

  const onSuggestionRequested = useConstant(() =>
    debounce(handleSuggestionsFetchRequested, debounceMilliseconds, {
      leading: true,
    }),
  );

  useEffect(() => {
    if (initial) {
      setSponsors(initial);
    }
  }, []);

  function renderSuggestion(suggestion, { query, isHighlighted }) {
    return (
      <MenuItem
        data-testid="shared-with-selector-menu-item"
        selected={isHighlighted}
        component="div"
      >
        <div>{suggestion.name}</div>
      </MenuItem>
    );
  }

  function renderInputComponent(inputProps) {
    const { classes, inputRef = () => {}, ref, ...other } = inputProps;

    return (
      <TextField
        data-testid="shared-with-selector"
        fullWidth
        autoComplete={false}
        InputLabelProps={{
          classes: {
            root: labelStyles.root,
            focused: labelStyles.root,
            shrink: labelStyles.root,
          },
        }}
        error={touched && invalid}
        helperText={touched && error}
        classes={{
          root: inputStyles.root,
          error: inputStyles.error,
        }}
        style={styles}
        InputProps={{
          inputRef: (node) => {
            ref(node);
            inputRef(node);
          },
        }}
        inputProps={{ autocomplete: "dont" }}
        shrink="true"
        variant="filled"
        {...other}
      />
    );
  }
  const handleSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const onSuggestionSelected = (
    event,
    { suggestion, suggestionValue, suggestionIndex, sectionIndex, method },
  ) => {
    if (suggestion && suggestion.id > -1) {
      if (sponsors && sponsors.length > 0) {
        dispatch(
          change(form, input.name, [
            ...sponsors
              .filter((r) => r && r.id != suggestion.id)
              .map((r) => r.id),
            suggestion.id,
          ]),
        );
      } else {
        dispatch(change(form, input.name, [suggestion.id]));
      }

      setNoMatch(false);
      setMatchSelected(true);
      setSponsors([
        ...sponsors.filter((r) => r && r.id != suggestion.id),
        suggestion,
      ]);
      setState({
        ...state,
        value: null,
        label: "",
      });
    } else {
      setState({
        ...state,
        value: null,
        label: "",
      });
    }
  };

  function getSuggestionValue(suggestion) {
    return suggestion;
  }

  const autosuggestProps = {
    renderInputComponent,
    onSuggestionsFetchRequested: onSuggestionRequested,
    onSuggestionsClearRequested: handleSuggestionsClearRequested,
    onSuggestionSelected: onSuggestionSelected,
    getSuggestionValue,
    renderSuggestion,
  };

  const handleChange =
    (name) =>
    (event, { newValue }) => {
      setState({
        ...state,
        value: newValue,
        label: newValue && newValue.name ? newValue.name : newValue,
      });
    };

  const handleDeleteChange = (data) => {
    if (data) {
      let updatedList = sponsors.filter((r) => r && r.id != data.id);
      setSponsors([...updatedList]);
      dispatch(
        change(
          form,
          input.name,
          [...updatedList].map((r) => r.id),
        ),
      );
    }
  };

  return (
    <div className={classes.root}>
      <Autosuggest
        data-testid="shared-with-selector-autosuggest"
        {...autosuggestProps}
        suggestions={suggestions}
        inputProps={{
          classes,
          id: input.name,
          label: label,
          value: state.label,
          onChange: handleChange(),
          onBlur: (e, x) => {
            setAnchorEl(null);
            if (noMatch || !matchSelected) {
              setState({
                ...state,
                value: null,
                label: "",
              });
              dispatch(
                change(
                  form,
                  input.name,
                  sponsors ? JSON.stringify(sponsors) : null,
                ),
              );
            }
          },
          inputRef: (node) => {
            setAnchorEl(node);
          },
        }}
        theme={{
          suggestionsList: classes.suggestionsList,
          suggestion: classes.suggestion,
        }}
        renderSuggestionsContainer={(options) => (
          <Popper
            data-testid="shared-with-selector-popper"
            anchorEl={anchorEl}
            open={Boolean(options.children)}
            style={{ zIndex: 1300 }}
            placement="bottom-start"
            modifiers={{
              flip: {
                enabled: false,
              },
              preventOverflow: {
                enabled: true,
                boundariesElement: "viewPort",
              },
            }}
          >
            <Paper
              data-testid="shared-with-selector-paper"
              square
              {...options.containerProps}
              className={classes.optionStyle}
              style={{
                maxHeight: anchorEl
                  ? window.innerHeight -
                    anchorEl.parentElement.getBoundingClientRect().bottom
                  : undefined,
                width: anchorEl
                  ? anchorEl.parentElement.clientWidth
                  : undefined,
                marginLeft: anchorEl
                  ? -(anchorEl.parentElement.clientWidth - anchorEl.clientWidth)
                  : undefined,
              }}
            >
              {options.children}
            </Paper>
          </Popper>
        )}
      />

      {sponsors && sponsors.length > 0 && (
        <div className={classes.paper}>
          {sponsors
            .sort((a, b) => (a.name > b.name ? 1 : -1))
            .map((data) => {
              return (
                data && (
                  <div
                    key={data.id}
                    className="selector-chip-wrapper"
                    style={{ padding: "2px", display: "inline-block" }}
                  >
                    <Chip
                      data-testid="shared-with-selector-chip"
                      avatar={
                        data.isPhiFree ? (
                          <Avatar
                            style={{
                              backgroundColor: "#fff",
                              height: "24px",
                              width: "24px",
                              marginLeft: "5px",
                            }}
                          >
                            <Lock style={{ color: "#000" }} />
                          </Avatar>
                        ) : (
                          <Avatar
                            style={{
                              backgroundColor: "#fff",
                              height: "24px",
                              width: "24px",
                              marginLeft: "5px",
                            }}
                          >
                            <LockOpen style={{ color: "#6ED1B2" }} />
                          </Avatar>
                        )
                      }
                      onDelete={() => handleDeleteChange(data)}
                      label={data.name}
                      className={classes.chip}
                    />
                  </div>
                )
              );
            })}
        </div>
      )}
    </div>
  );
};

export default ({
  label,
  name,
  validators,
  suggestions,
  maxLength = 255,
  noMatchText = "No Matches Found",
  protocolId,
  allowArchived,
  ...other
}) => {
  return (
    <Field
      name={name}
      component={SponsorsMultiSelectComponent}
      label={label}
      maxLength={maxLength}
      fullWidth
      validate={validators}
      noMatchText={noMatchText}
      protocolId={protocolId}
      allowArchived={allowArchived}
      {...other}
    />
  );
};
